/**
 * Onboarding brand-draft sessionStorage keys — namespaced per workspace.
 *
 * Two things live in sessionStorage during onboarding:
 *   - the in-flight generation job  (job_id + dumpMode)  → resume a running job
 *   - the finished-but-unconfirmed draft                 → resume the review step
 *
 * Both used to be stored under GLOBAL keys ("lm_brand_job" / "lm_brand_draft").
 * Two fixes already narrowed that: logout() clears them, and AuthContext.checkAuth
 * clears them when the signed-in USER changes. Neither closes the remaining hole:
 * ONE user with TWO workspaces. A draft started in workspace A restored in
 * workspace B and could be confirmed into the wrong workspace — the brand, its
 * profile, ICPs and keywords all landing under the wrong tenant.
 *
 * So the key carries the workspace_id. A draft is only ever readable by the
 * workspace that created it.
 *
 * Deliberately NOT fixed by clearing on every mount: a same-user, same-workspace
 * refresh keeping its own draft is a designed resume path (Onboarding.jsx reads
 * these on mount so a refresh in the review→confirm window doesn't drop the user
 * back to an empty website step, which would then 429 on the 24h cooldown).
 * Namespacing keeps that path intact and only breaks the cross-workspace one.
 *
 * When workspace_id is not yet known, callers must NOT read or write — see
 * `hasBrandDraftScope`. Guessing a key would either resurrect another
 * workspace's draft or write one that is never found again.
 */

// Legacy global keys. Still listed in the clear helper so a session that
// straddles this change gets its un-namespaced leftovers purged.
const LEGACY_KEYS = ["lm_brand_job", "lm_brand_draft"];

const JOB_PREFIX = "lm_brand_job:";
const DRAFT_PREFIX = "lm_brand_draft:";

/** True when we know which workspace we're in and may touch draft storage. */
export function hasBrandDraftScope(workspaceId) {
  return typeof workspaceId === "string" && workspaceId.length > 0;
}

/**
 * `flow` separates the FIRST-RUN onboarding from the ADD-BRAND run (`?new=1`)
 * within one workspace.
 *
 * Without it both write `lm_brand_draft:<ws>`, and the two flows are not
 * interchangeable: an add-brand draft carries its OWN category and market
 * (that is the entire point of `?new=1`), while a first-run draft carries the
 * workspace's. Restoring one as the other silently swaps a brand's market —
 * the exact defect the backend's per-brand `business_category` / `market_scope`
 * change was made to prevent, reintroduced on the client through a shared
 * storage key.
 *
 * Undefined/empty `flow` keeps the original key byte-for-byte, so existing
 * sessions mid-onboarding are unaffected by this change.
 */
function suffix(flow) {
  return flow ? `:${flow}` : "";
}

/** sessionStorage key for the in-flight generation job of this workspace. */
export function genSessionKey(workspaceId, flow) {
  return `${JOB_PREFIX}${workspaceId}${suffix(flow)}`;
}

/** sessionStorage key for the finished-but-unconfirmed draft of this workspace. */
export function genDraftKey(workspaceId, flow) {
  return `${DRAFT_PREFIX}${workspaceId}${suffix(flow)}`;
}

/** The `flow` discriminator for the add-a-second-brand run. */
export const FLOW_NEW_BRAND = "new";

/**
 * Remove EVERY workspace's draft + job keys (and the legacy global ones).
 * Used on logout and on a user change — at that point no draft in this tab
 * belongs to whoever is now signed in, whichever workspace it came from.
 */
export function clearAllBrandDrafts() {
  try {
    const drop = [...LEGACY_KEYS];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith(JOB_PREFIX) || k.startsWith(DRAFT_PREFIX))) drop.push(k);
    }
    drop.forEach((k) => sessionStorage.removeItem(k));
  } catch (_) {}
}
